// pms-adapters / supabase / receptionPayments — the desk's payment log
// (P3-T, PMS-TERMINAL-LINK-BRIEF §8.6/§8.7).
//
// Same shape as `vegaMirror.ts`: a direct Supabase adapter, no repository
// split — this port has one consumer (Encaissement) and one backing system,
// so a swappable-source seam would buy nothing. Unlike vegaMirror it is
// mostly WRITES, so it follows services.ts's L2 convention instead
// (ADR-0013 §4): `gate()` before every write RPC, buying a French refusal
// before the round trip. It is NOT the security boundary — every RPC below
// gates itself with `require_capability('pms.payment.write')` and the table's
// RLS policy gates every read; this is the courtesy layer, not a second one.

import {
  CAPABILITIES,
  assertCapability,
  type ActorSource,
} from '@lemontaigu/platform';
import {
  err,
  ok,
  type CreateTerminalIntentInput,
  type Err,
  type Instant,
  type IsoDate,
  type ReceptionPayment,
  type ReceptionPaymentId,
  type ReceptionPaymentsService,
  type RecordReceptionPaymentInput,
  type Result,
  type RoomStayLookup,
  type ScopeSource,
  type Terminal,
  type TerminalIntentId,
  type TerminalTransactionResult,
} from '@lemontaigu/pms-core';
import {
  fromList,
  fromResult,
  fromVoid,
  scopedQuery,
  scopedRpcProperty,
  type SupabaseClient,
} from './support.js';

/** The gate, as in services.ts — `null` means "allowed". */
function gate(actor: ActorSource): Err | null {
  const check = assertCapability(actor.current(), CAPABILITIES.PMS_PAYMENT_WRITE);
  return check.ok ? null : err('forbidden', check.message);
}

interface TerminalRow {
  id: number;
  label: string;
  ip_address: string;
  port: number;
  bridge_seen_at: string | null;
}

interface PaymentRow {
  id: number;
  resa: string | null;
  room_number: string | null;
  tender_type: ReceptionPayment['tenderType'];
  amount_cents: number;
  status: ReceptionPayment['status'];
  terminal_transaction_id: number | null;
  settles_suspense_id: number | null;
  captured_at: string;
  vega_keyed: boolean;
  vega_keyed_at: string | null;
}

interface LookupRow {
  resa: string;
  party_label: string;
  checkin: string;
  checkout: string;
  property_code: string;
}

interface TransactionRow {
  id: number;
  intent_id: number;
  amount_cents: number;
  status: TerminalTransactionResult['status'];
  authorisation_ref: string | null;
  card_masked: string | null;
}

interface IntentRow {
  status: 'pending' | 'claimed' | 'done' | 'expired';
  claimed_at: string | null;
}

// §8.8m — the screen's clock, derived from the server's (migration
// 20260926040100). The ceiling must stay above every server-side path:
// claim window (60 s) + claimed→expired (300 s) + one sweep (~60 s) + margin.
const TERMINAL_CLAIM_WINDOW_MS = 60_000;
const TERMINAL_WITHDRAW_AFTER_MS = TERMINAL_CLAIM_WINDOW_MS + 5_000;
const TERMINAL_WAIT_CEILING_MS = TERMINAL_CLAIM_WINDOW_MS + 300_000 + 60_000 + 30_000;

const toTerminal = (r: TerminalRow): Terminal => ({
  id: r.id,
  label: r.label,
  ipAddress: r.ip_address,
  port: r.port,
  bridgeSeenAt: r.bridge_seen_at as Instant | null,
});

const toPayment = (r: PaymentRow): ReceptionPayment => ({
  id: r.id,
  resa: r.resa,
  roomNumber: r.room_number,
  tenderType: r.tender_type,
  amountCents: r.amount_cents,
  status: r.status,
  terminalTransactionId: r.terminal_transaction_id,
  settlesSuspenseId: r.settles_suspense_id,
  capturedAt: r.captured_at as Instant,
  vegaKeyed: r.vega_keyed,
  vegaKeyedAt: r.vega_keyed_at as Instant | null,
});

const toTransactionResult = (r: TransactionRow): TerminalTransactionResult => ({
  id: r.id,
  intentId: r.intent_id,
  amountCents: r.amount_cents,
  status: r.status,
  authorisationRef: r.authorisation_ref,
  cardMasked: r.card_masked,
});

/**
 * Today's [start, end) bound, as Europe/Paris civil-day boundaries — NOT the
 * session timezone's midnight. `captured_at` is timestamptz; comparing it
 * against a bare "YYYY-MM-DDT00:00:00" lets Postgres interpret the string in
 * whatever timezone the connection holds (Supabase's default is UTC), which
 * is wrong by one or two hours around Paris midnight. The rest of this
 * codebase has been burned by exactly this class of mistake before (the board
 * planning fix was "todayIsoParis everywhere") — the fix here is the same
 * idea without adding a package dependency for one date computation.
 */
function parisTodayBounds(now: Date = new Date()): { from: string; to: string } {
  const dateFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const today = dateFmt.format(now); // 'YYYY-MM-DD' in Paris
  // A midday UTC anchor for "tomorrow" survives the DST-transition day: adding
  // 24h to a midnight instant can land on the WRONG Paris calendar day right
  // when the clocks change, but midday never crosses that boundary.
  const anchor = new Date(`${today}T12:00:00Z`);
  const tomorrow = dateFmt.format(new Date(anchor.getTime() + 24 * 60 * 60 * 1000));

  // The UTC offset Paris is currently observing (+01:00 or +02:00), read from
  // the actual instant rather than hard-coded — this is what makes the bound
  // correct on both sides of a DST change without a timezone-data dependency.
  const utcMillis = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();
  const parisMillis = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' })).getTime();
  const offsetMin = Math.round((parisMillis - utcMillis) / 60000);
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const offset = `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;

  return { from: `${today}T00:00:00${offset}`, to: `${tomorrow}T00:00:00${offset}` };
}

export class SupabaseReceptionPayments implements ReceptionPaymentsService {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
    private readonly actor: ActorSource,
  ) {}

  /** The terminals that SERVE the scope's property (§8.8n) — one terminal
   *  may serve every property its operator runs, so this reads through
   *  `pms_terminal_property`, not the terminal's own registration. */
  async terminals(): Promise<Result<Terminal[]>> {
    const r = await scopedQuery(
      this.db
        .from('pms_terminal')
        .select('id, label, ip_address, port, bridge_seen_at, pms_terminal_property!inner(property_code)')
        .order('id'),
      this.scope.current(),
      'pms_terminal_property.property_code',
    );
    return fromList<TerminalRow, Terminal>(r as { data: TerminalRow[] | null; error: typeof r.error }, toTerminal);
  }

  async lookupRoom(roomNumber: string): Promise<Result<RoomStayLookup[]>> {
    const r = await this.db.rpc('pms_lookup_room_stay', { p_room_number: roomNumber });
    // Every row, never `[0]`: the RPC returns one stay per établissement in
    // reach (U0), so a second row is a second building — the caller asks.
    return fromList<LookupRow, RoomStayLookup>(
      r as { data: LookupRow[] | null; error: typeof r.error },
      (row) => ({
        resa: row.resa,
        partyLabel: row.party_label,
        checkin: row.checkin as IsoDate,
        checkout: row.checkout as IsoDate,
        propertyCode: row.property_code,
      }),
    );
  }

  async listToday(): Promise<Result<ReceptionPayment[]>> {
    const { from, to } = parisTodayBounds();
    const r = await scopedQuery(
      this.db
        .from('pms_reception_payment')
        .select(
          'id, resa, room_number, tender_type, amount_cents, status, terminal_transaction_id, ' +
            'settles_suspense_id, captured_at, vega_keyed, vega_keyed_at',
        )
        .gte('captured_at', from)
        .lt('captured_at', to)
        .order('captured_at', { ascending: false }),
      this.scope.current(),
    );
    return fromList<PaymentRow, ReceptionPayment>(
      r as { data: PaymentRow[] | null; error: typeof r.error },
      toPayment,
    );
  }

  async recordPayment(input: RecordReceptionPaymentInput): Promise<Result<ReceptionPaymentId>> {
    const denied = gate(this.actor);
    if (denied) return denied;

    const r = await this.db.rpc('pms_record_reception_payment', {
      p_property_code: input.propertyCode ?? scopedRpcProperty(this.scope.current()),
      p_tender_type: input.tenderType,
      p_room_number: input.roomNumber ?? null,
      p_resa: input.resa ?? null,
      // Ignored server-side for 'card' (the RPC takes the terminal's amount
      // instead) — passed through as given rather than special-cased here,
      // which keeps this adapter a thin wrapper and leaves the ONE place that
      // rule is enforced exactly where 0187 put it.
      p_amount_cents: input.amountCents ?? null,
      p_terminal_transaction_id: input.terminalTransactionId ?? null,
      p_settles_suspense_id: input.settlesSuspenseId ?? null,
      // §8.8p — the tab it was taken on; 20260927010100 added the argument.
      p_taken_on: input.takenOn ?? null,
    });
    return fromResult<ReceptionPaymentId>(r);
  }

  async markVegaKeyed(id: ReceptionPaymentId): Promise<Result<void>> {
    const denied = gate(this.actor);
    if (denied) return denied;

    return fromVoid(await this.db.rpc('pms_mark_vega_keyed', { p_payment_id: id }));
  }

  async cancelSuspense(id: ReceptionPaymentId): Promise<Result<ReceptionPaymentId>> {
    const denied = gate(this.actor);
    if (denied) return denied;

    const r = await this.db.rpc('pms_cancel_reception_payment', { p_payment_id: id });
    return fromResult<ReceptionPaymentId>(r);
  }

  async createTerminalIntent(input: CreateTerminalIntentInput): Promise<Result<TerminalIntentId>> {
    const denied = gate(this.actor);
    if (denied) return denied;

    const r = await this.db.rpc('pms_create_terminal_intent', {
      p_terminal_id: input.terminalId,
      p_outlet: input.outlet,
      p_amount_cents: input.amountCents,
      p_room_number: input.roomNumber ?? null,
      p_resa: input.resa ?? null,
      p_property_code: input.propertyCode ?? null,
    });
    return fromResult<TerminalIntentId>(r);
  }

  /**
   * Waits for the ONE `pms_terminal_transaction` row this intent produces,
   * following the INTENT's own status rather than a fixed clock (§8.8m) —
   * because « réessayez » is only safe when the terminal was never asked:
   *
   * - bridge never took it → withdrawn after the claim window → `transient`,
   *   retryable: no debit can happen, a retry is safe;
   * - bridge took it and it expired without an answer, or the ceiling passes
   *   → `integration`, NOT retryable: the card may have been charged.
   *
   * `pms_record_terminal_transaction` is not executable by the browser
   * (revoked 20260926040100) — this method can only ever listen, so it
   * cannot manufacture the result it is waiting for.
   */
  awaitTerminalResult(intentId: TerminalIntentId): Promise<Result<TerminalTransactionResult>> {
    return new Promise((resolve) => {
      let settled = false;
      let claimed = false;
      const timers: ReturnType<typeof setTimeout>[] = [];
      const finish = (result: Result<TerminalTransactionResult>) => {
        if (settled) return;
        settled = true;
        timers.forEach(clearTimeout);
        void channel.unsubscribe();
        resolve(result);
      };

      const notAsked = () =>
        err('transient', 'Le TPE n’a pas reçu la demande — aucun débit.', { retryable: true });
      const unknown = () =>
        err('integration', 'Le TPE a reçu la demande sans répondre — le débit a pu avoir lieu.', {
          retryable: false,
        });

      const readTransaction = async (): Promise<boolean> => {
        const r = await this.db
          .from('pms_terminal_transaction')
          .select('id, intent_id, amount_cents, status, authorisation_ref, card_masked')
          .eq('intent_id', intentId)
          .maybeSingle();
        if (!r.data) return false;
        finish(ok(toTransactionResult(r.data as TransactionRow)));
        return true;
      };

      const onIntent = async (row: IntentRow) => {
        if (row.claimed_at) claimed = true;
        // done: the transaction was written in the same database transaction,
        // so it is there to read even if its INSERT event was missed.
        if (row.status === 'done') await readTransaction();
        else if (row.status === 'expired' && !(await readTransaction())) {
          finish(row.claimed_at ? unknown() : notAsked());
        }
      };

      const readIntent = async () => {
        const r = await this.db
          .from('pms_terminal_intent')
          .select('status, claimed_at')
          .eq('id', intentId)
          .maybeSingle();
        if (r.data) await onIntent(r.data as IntentRow);
      };

      const channel = this.db
        .channel(`reception-payments:terminal-intent-${intentId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'pms_terminal_transaction',
            filter: `intent_id=eq.${intentId}`,
          },
          (payload: { new: TransactionRow }) => finish(ok(toTransactionResult(payload.new))),
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'pms_terminal_intent',
            filter: `id=eq.${intentId}`,
          },
          (payload: { new: IntentRow }) => void onIntent(payload.new),
        )
        // Anything that happened before the subscription was live.
        .subscribe((status: string) => {
          if (status === 'SUBSCRIBED') void readIntent();
        });

      // Past the claim window the bridge will never take it: take it back.
      // FALSE means the bridge has it after all — keep waiting for its answer.
      timers.push(
        setTimeout(() => {
          if (settled || claimed) return;
          void this.db
            .rpc('pms_withdraw_terminal_intent', { p_intent_id: intentId })
            .then((r: { data: unknown; error: unknown }) => {
              if (!r.error && r.data === true) finish(notAsked());
              else claimed = true;
            });
        }, TERMINAL_WITHDRAW_AFTER_MS),
      );

      // Every server-side path has ended by now; if nothing said so, the
      // outcome is unknown, never « réessayez ».
      timers.push(
        setTimeout(() => {
          void readTransaction().then((found) => {
            if (!found) finish(unknown());
          });
        }, TERMINAL_WAIT_CEILING_MS),
      );
    });
  }
}
